import apiClient from './apiClient';

const mapProduct = (product) => {
  if (!product) return null;
  const onSale = product.on_sale;
  const currentPrice = product.price;
  const originalPrice = onSale && product.sale_price ? product.sale_price : null;

  return {
    id: product.id,
    name: product.name,
    slug: product.slug,
    description: product.description,
    price: currentPrice,
    originalPrice,
    onSale,
    isNew: product.is_new,
    isBestSeller: product.is_best_seller,
    isFeatured: product.is_featured,
    stock: product.stock,
    category: product.category_id,
    subcategory: product.subcategory_id,
    sizeGuideId: product.size_guide_id || null,
    sizes: product.sizes || [],
    colors: product.colors || [],
    colorNames: product.color_names || [],
    material: product.material || '',
    care: product.care || '',
    images: (product.images || [])
      .slice()
      .sort((a, b) => {
        if (typeof a === 'string' || typeof b === 'string') return 0;
        return (a.order ?? 0) - (b.order ?? 0);
      })
      .map((image) => (typeof image === 'string' ? image : image.url || image)),
    createdAt: product.created_at,
    updatedAt: product.updated_at,
  };
};

/**
 * Get all products
 * @returns {Promise<Array>} Array of products
 */
export const getProducts = async () => {
  try {
    const response = await apiClient.get('/products');
    return response.data.map(mapProduct);
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

/**
 * Get a single product by ID
 * @param {string} productId - Product ID
 * @returns {Promise<Object>} Product object
 */
export const getProductById = async (productId) => {
  try {
    const response = await apiClient.get(`/products/${productId}`);
    return mapProduct(response.data);
  } catch (error) {
    console.error('Error fetching product:', error);
    throw error;
  }
};

/**
 * Get products by category
 * @param {string} categoryId - Category ID
 * @returns {Promise<Array>} Array of products
 */
export const getProductsByCategory = async (categoryId) => {
  try {
    const response = await apiClient.get('/products', {
      params: { category_id: categoryId },
    });
    return response.data.map(mapProduct);
  } catch (error) {
    console.error('Error fetching products by category:', error);
    throw error;
  }
};

/**
 * Get products by category and subcategory
 * @param {string} categoryId - Category ID
 * @param {string} subcategoryId - Subcategory ID
 * @returns {Promise<Array>} Array of products
 */
export const getProductsBySubcategory = async (categoryId, subcategoryId) => {
  try {
    const response = await apiClient.get('/products', {
      params: { category_id: categoryId, subcategory_id: subcategoryId },
    });
    return response.data.map(mapProduct);
  } catch (error) {
    console.error('Error fetching products by subcategory:', error);
    throw error;
  }
};

/**
 * Search products by name or description
 * @param {string} searchTerm - Search term
 * @returns {Promise<Array>} Array of matching products
 */
export const searchProducts = async (searchTerm) => {
  try {
    const response = await apiClient.get('/products/search', {
      params: { q: searchTerm },
    });
    return response.data.map(mapProduct);
  } catch (error) {
    console.error('Error searching products:', error);
    throw error;
  }
};

/**
 * Get products on sale
 * @returns {Promise<Array>} Array of products on sale
 */
export const getSaleProducts = async () => {
  try {
    const response = await apiClient.get('/products', {
      params: { on_sale: true },
    });
    return response.data.map(mapProduct);
  } catch (error) {
    console.error('Error fetching sale products:', error);
    throw error;
  }
};

/**
 * Get new arrival products
 * @returns {Promise<Array>} Array of new products
 */
export const getNewArrivals = async () => {
  try {
    const response = await apiClient.get('/products', {
      params: { is_new: true, page_size: 20 },
    });
    return response.data.map(mapProduct);
  } catch (error) {
    console.error('Error fetching new arrivals:', error);
    throw error;
  }
};

/**
 * Get bestseller products
 * @returns {Promise<Array>} Array of bestseller products
 */
export const getBestSellers = async () => {
  try {
    const response = await apiClient.get('/products', {
      params: { is_best_seller: true },
    });
    return response.data.map(mapProduct);
  } catch (error) {
    console.error('Error fetching bestsellers:', error);
    throw error;
  }
};

/**
 * Get featured products
 * @returns {Promise<Array>} Array of featured products
 */
export const getFeaturedProducts = async () => {
  try {
    const response = await apiClient.get('/products', {
      params: { is_featured: true },
    });
    return response.data.map(mapProduct);
  } catch (error) {
    console.error('Error fetching featured products:', error);
    throw error;
  }
};

/**
 * Get related products (same category, excluding current product)
 * @param {string} productId - Current product ID
 * @param {string} categoryId - Category ID
 * @param {number} limitCount - Maximum number of products to return
 * @returns {Promise<Array>} Array of related products
 */
export const getRelatedProducts = async (productId, categoryId, limitCount = 4) => {
  try {
    const response = await apiClient.get('/products/related', {
      params: { product_id: productId, category_id: categoryId, limit: limitCount },
    });
    return response.data.map(mapProduct);
  } catch (error) {
    console.error('Error fetching related products:', error);
    throw error;
  }
};
